import { useState, useCallback, useEffect } from "react";

const SESSION_LOCATION_KEY = "eatzy_session_location";
const SYNC_EVENT = "eatzy-session-location-updated";

interface SessionLocation {
  name: string;
  latitude?: number;
  longitude?: number;
}

function getStoredLocation(): SessionLocation | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = sessionStorage.getItem(SESSION_LOCATION_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    // ignore
  }
  return null;
}

function storeLocation(loc: SessionLocation) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(SESSION_LOCATION_KEY, JSON.stringify(loc));
  } catch (e) {
    // ignore
  }
}

export function clearSessionLocation() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(SESSION_LOCATION_KEY);
    window.dispatchEvent(new CustomEvent(SYNC_EVENT));
  } catch (e) {
    // ignore
  }
}

export function useSessionLocation() {
  const [selectedLocationName, setSelectedLocationNameState] = useState<string>(() => getStoredLocation()?.name ?? "");
  const [customLatitude, setCustomLatitudeState] = useState<number | undefined>(() => getStoredLocation()?.latitude);
  const [customLongitude, setCustomLongitudeState] = useState<number | undefined>(() => getStoredLocation()?.longitude);

  useEffect(() => {
    const handleSync = () => {
      const loc = getStoredLocation();
      setSelectedLocationNameState(loc?.name ?? "");
      setCustomLatitudeState(loc?.latitude);
      setCustomLongitudeState(loc?.longitude);
    };
    window.addEventListener(SYNC_EVENT, handleSync);
    return () => window.removeEventListener(SYNC_EVENT, handleSync);
  }, []);

  const setSessionLocation = useCallback((name: string, lat?: number, lng?: number) => {
    setSelectedLocationNameState(name);
    setCustomLatitudeState(lat);
    setCustomLongitudeState(lng);
    if (name) {
      storeLocation({ name, latitude: lat, longitude: lng });
    } else {
      clearSessionLocation();
      return;
    }
    window.dispatchEvent(new CustomEvent(SYNC_EVENT));
  }, []);

  return {
    selectedLocationName,
    customLatitude,
    customLongitude,
    setSessionLocation,
  };
}
